import i18n from "../i18n.js";

// Traduz erros do Better Auth para mensagens claras para o usuário final.
// O client pode expor `error.code` (chave estável, ex.: INVALID_EMAIL_OR_PASSWORD)
// e/ou `error.message` (texto em inglês). Mapeamos pelo code; se ele não vier,
// casamos pelo texto em inglês conhecido. Erros do nosso próprio back (ex.: o 422
// de falha de entrega no reset/convite) já chegam em PT-BR — nesse caso
// devolvemos a própria message. Sem code conhecido nem match, cai no fallback.

type AuthErrorLike =
  | { code?: string; message?: string; status?: number; statusText?: string }
  | null
  | undefined;

// code → mensagem PT-BR. Cobre os erros que um usuário encontra nos fluxos de
// login e senha; o resto cai no fallback informado por quem chama.
const BY_CODE: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: "E-mail ou senha incorretos.",
  INVALID_PASSWORD: "Senha incorreta.",
  INVALID_EMAIL: "E-mail inválido.",
  USER_NOT_FOUND: "Não encontramos uma conta com esse e-mail.",
  CREDENTIAL_ACCOUNT_NOT_FOUND: "Esta conta não usa login por senha.",
  EMAIL_NOT_VERIFIED: "Confirme seu e-mail antes de entrar.",
  PASSWORD_TOO_SHORT: "A senha é muito curta.",
  PASSWORD_TOO_LONG: "A senha é muito longa.",
  USER_ALREADY_EXISTS: "Já existe uma conta com esse e-mail.",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "Já existe uma conta com esse e-mail. Use outro.",
  INVALID_TOKEN: "Este link é inválido ou já foi usado.",
  TOKEN_EXPIRED: "Este link expirou. Solicite um novo.",
  SESSION_EXPIRED: "Sua sessão expirou. Entre novamente.",
  SESSION_NOT_FRESH: "Por segurança, entre novamente para concluir esta ação.",
  ACCOUNT_NOT_FOUND: "Conta não encontrada.",
  PASSWORD_ALREADY_SET: "Esta conta já possui uma senha definida.",
  USER_ALREADY_HAS_PASSWORD: "Esta conta já possui uma senha definida.",
};

// Texto em inglês (normalizado) → code, para quando o client só trouxer message.
const CODE_BY_MESSAGE: Record<string, string> = {
  "invalid email or password": "INVALID_EMAIL_OR_PASSWORD",
  "invalid password": "INVALID_PASSWORD",
  "invalid email": "INVALID_EMAIL",
  "user not found": "USER_NOT_FOUND",
  "credential account not found": "CREDENTIAL_ACCOUNT_NOT_FOUND",
  "email not verified": "EMAIL_NOT_VERIFIED",
  "password too short": "PASSWORD_TOO_SHORT",
  "password too long": "PASSWORD_TOO_LONG",
  "user already exists.": "USER_ALREADY_EXISTS",
  "invalid token": "INVALID_TOKEN",
  "token expired": "TOKEN_EXPIRED",
  "session expired. re-authenticate to perform this action.": "SESSION_EXPIRED",
};

export function authErrorMessage(error: AuthErrorLike, fallback?: string): string {
  const resolvedFallback = fallback ?? i18n.t("authErrors.fallback");
  if (!error) return resolvedFallback;

  // Rate limit do Better Auth não tem code próprio — vem como HTTP 429.
  if (error.status === 429) {
    return i18n.t("authErrors.rateLimit");
  }

  const code =
    error.code ?? (error.message ? CODE_BY_MESSAGE[error.message.trim().toLowerCase()] : undefined);
  if (code && BY_CODE[code]) {
    return i18n.t(`authErrors.codes.${code}`, { defaultValue: BY_CODE[code] });
  }

  // Mensagens do nosso back já vêm em PT-BR (ex.: 422 de falha de entrega).
  return error.message ?? resolvedFallback;
}
